import { apiClient } from "./apiClient";

/**
 * User management -- wired to the real /management/users* routes in
 * api/functions/admin.py. Every one of these is require_jit=True (reviewing
 * or acting on a user account is itself sensitive) -- a 403 with
 * { error: "jit_access_required" } routes the admin app back to
 * AccessRequestPage via apiClient.js's response interceptor rather than
 * surfacing here as a normal error.
 *
 * list/get/suspend/unsuspend/forceLogout/overrideTier all hit real
 * endpoints now. bulkInvite, activityHistory's dedicated shape, riskScore,
 * trusted-device management, and geofence override still have no backend
 * support (see UserManagementPage.jsx's GapNotice blocks) -- those calls are
 * left pointing at the routes a future backend would need, same as before,
 * and continue to fail gracefully in the UI.
 */
export const userManagementApi = {
  list: (params) => apiClient.get("/management/users", { params }).then((r) => r.data),
  // Creates the app.Users row ONLY. The Entra identity is separate and is
  // not touched -- see api/functions/admin.py create_user.
  create: (payload) => apiClient.post("/management/users", payload).then((r) => r.data),
  get: (userId) => apiClient.get(`/management/users/${userId}`).then((r) => r.data),

  bulkInvite: (rows) => apiClient.post("/management/users/bulk-invite", { invites: rows }).then((r) => r.data),

  overrideTier: (userId, tier) => apiClient.put(`/management/users/${userId}/tier`, { new_tier: tier }).then((r) => r.data),

  suspend: (userId) => apiClient.post(`/management/users/${userId}/suspend`).then((r) => r.data),
  unsuspend: (userId) => apiClient.post(`/management/users/${userId}/unsuspend`).then((r) => r.data),
  // days omitted = permanent. Distinct from suspend: a ban blocks sign-in
  // and leaves the account and its history intact (#305).
  ban: (userId, reason, days) =>
    apiClient.post(`/management/users/${userId}/ban`, { reason, days: days ?? undefined }).then((r) => r.data),
  unban: (userId) => apiClient.post(`/management/users/${userId}/unban`).then((r) => r.data),
  listBans: (userId) => apiClient.get(`/management/users/${userId}/bans`).then((r) => r.data),

  forceLogout: (userId) => apiClient.post(`/management/users/${userId}/force-logout`).then((r) => r.data),

  activityHistory: (userId) => apiClient.get("/management/audit-log", { params: { user_id: userId } }).then((r) => r.data),
  riskScore: (userId) => apiClient.get(`/management/users/${userId}/risk-score`).then((r) => r.data),

  listTrustedDevices: (userId) => apiClient.get(`/management/users/${userId}/trusted-devices`).then((r) => r.data),
  revokeTrustedDevice: (userId, deviceId) => apiClient.delete(`/management/users/${userId}/trusted-devices/${deviceId}`).then((r) => r.data),

  overrideGeofence: (userId, reason, expiresAt) =>
    apiClient.post(`/management/users/${userId}/geofence-override`, { reason, expires_at: expiresAt }).then((r) => r.data),
};
